import React, { useRef, useState } from 'react';
import {
  View,
  ScrollView,
  TouchableOpacity,
  StyleSheet,
  Image,
  FlatList,
  Dimensions,
  Animated,
  Platform
} from 'react-native';
import { StatusBar } from 'expo-status-bar';
import { Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { AuthContext } from '../../contexts/AuthContext';
import { H1, H2, Body1, Body2, Caption, ButtonText } from '../../components/common/TextStyles';
import { useTheme, getShadow } from '../../theme/useTheme';
import VideoModal from '../../components/common/VideoModal';
import { getImageUrl } from '../../utils/imageUtils';
import { getData } from '../../services/api';
import Skeleton from '../../components/common/Skeleton';

const { width: SCREEN_WIDTH, height: SCREEN_HEIGHT } = Dimensions.get('window');

const EventsScreen = ({ navigation, route }) => {
  const { user } = React.useContext(AuthContext);
  const insets = useSafeAreaInsets();
  const [videoModalVisible, setVideoModalVisible] = useState(false);
  const [currentVideoUrl, setCurrentVideoUrl] = useState('');
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState(route.params?.filter === 'joined' ? 'Joined' : 'All');
  const scrollY = useRef(new Animated.Value(0)).current;

  const isMentorOrAdmin = user?.role === 'admin' || user?.isMentor;

  React.useEffect(() => {
    fetchEvents();
  }, [activeCategory]);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      if (activeCategory === 'Joined') {
        const data = await getData('/events/passbook');
        // Structure for passbook is different (contains mission/event wrapper)
        if (data && Array.isArray(data)) {
          const joinedEvents = data.map(m => m.event).filter(Boolean);
          setEvents(joinedEvents);
        } else {
          setEvents([]);
        }
      } else {
        const vibeParam = activeCategory === 'All' ? '' : `?vibe=${activeCategory}`;
        const data = await getData(`/events/radar${vibeParam}`);
        setEvents(data || []);
      }
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleVideoPress = (videoUrl) => {
    setCurrentVideoUrl(videoUrl);
    setVideoModalVisible(true);
  };

  const categories = [
    { id: '1', name: 'All' },
    { id: '6', name: 'Joined' },
    { id: '2', name: 'Skill Up' },
    { id: '3', name: 'Live Talks' },
    { id: '4', name: 'Seminar' },
    { id: '5', name: 'Meetups' },
  ];

  const headerHeight = 180;

  const headerOpacity = scrollY.interpolate({
    inputRange: [0, 100],
    outputRange: [1, 0],
    extrapolate: 'clamp',
  });

  const stickyHeaderOpacity = scrollY.interpolate({
    inputRange: [50, 100],
    outputRange: [0, 1],
    extrapolate: 'clamp',
  });

  const renderEventItem = ({ item }) => {
    const formatDateCard = (d) => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const dateStr = item.event_end_date && new Date(item.event_end_date).toDateString() !== new Date(item.event_date).toDateString()
      ? `${formatDateCard(item.event_date)} - ${formatDateCard(item.event_end_date)}`
      : new Date(item.event_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

    return (
      <TouchableOpacity
        style={styles.eventCard}
        onPress={() => navigation.navigate('EventDetails', { eventId: item.id })}
        activeOpacity={0.9}
      >
        <View style={styles.cardImageContainer}>
          <Image
            source={{ uri: getImageUrl(item.image_url) || 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=800' }}
            style={styles.eventImage}
          />
          <LinearGradient
            colors={['transparent', 'rgba(0,0,0,0.6)']}
            style={styles.imageOverlay}
          />
          <View style={styles.categoryBadgeStatic}>
            <Caption style={styles.categoryBadgeText}>{item.vibe || 'EVENT'}</Caption>
          </View>
          {new Date(item.event_end_date || item.event_date) < new Date() && (
            <View style={styles.pastBadge}>
              <Caption style={styles.pastBadgeText}>PAST ACTIVITY</Caption>
            </View>
          )}
        </View>

        <View style={styles.eventInfo}>
          <View style={styles.titleRow}>
            <View style={{ flex: 1 }}>
              <H2 style={styles.eventTitleText} numberOfLines={1}>{item.title}</H2>
              <Caption style={styles.vibeTextCompact} >{item.vibe || 'EVENT'}</Caption>
            </View>
            <View style={styles.pointsBadgeCompact}>
              <Caption style={styles.pointsTextCompact}>+{item.points_reward} XP</Caption>
            </View>
          </View>

          <View style={styles.compactDetails}>
            <View style={styles.detailItem}>
              <Ionicons name="calendar-outline" size={10} color="#6366F1" />
              <Caption style={styles.detailText}>
                {dateStr}
                {item.start_time && item.end_time && (
                  ` • ${new Date(item.start_time).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })} - ${new Date(item.end_time).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`
                )}
              </Caption>
            </View>
            <View style={styles.detailItem}>
              <Ionicons name={item.type === 'virtual' ? 'globe-outline' : 'location-outline'} size={10} color="#9CA3AF" />
              <Caption style={styles.detailText} numberOfLines={1}>
                {item.location_name || item.virtual_link || 'TBA'}
              </Caption>
            </View>
          </View>
        </View>
      </TouchableOpacity>
    );
  };

  return (
    <View style={styles.container}>
      <StatusBar style="light" />

      {/* Hero Header Area */}
      <View style={[styles.headerBg, { height: headerHeight }]}>
        <LinearGradient
          colors={['#7c3aed', '#6366f1']}
          style={StyleSheet.absoluteFill}
        />
        <View style={styles.auraContainer}>
          <View style={[styles.aura, styles.aura1]} />
          <View style={[styles.aura, styles.aura2]} />
        </View>
      </View>

      {/* Sticky Top Bar */}
      <Animated.View
        style={[
          styles.stickyTopBar,
          {
            paddingTop: insets?.top || 0,
            opacity: stickyHeaderOpacity,
            transform: [{
              translateY: scrollY.interpolate({
                inputRange: [0, 80, 81],
                outputRange: [-200, -200, 0],
                extrapolate: 'clamp'
              })
            }]
          }
        ]}
        pointerEvents="box-none"
      >
        <LinearGradient colors={['#7c3aed', '#6366f1']} style={StyleSheet.absoluteFill} />
        <View style={styles.stickyContent}>
          <TouchableOpacity onPress={() => navigation.goBack()} style={styles.miniBackBtn}>
            <Ionicons name="arrow-back" size={20} color="#FFFFFF" />
          </TouchableOpacity>
          <H2 style={styles.stickyTitle}>Events Feed</H2>
          <View style={styles.headerIcons}>
            {isMentorOrAdmin && (
              <TouchableOpacity onPress={() => navigation.navigate('EventAdd')} style={styles.miniAddBtn}>
                <Ionicons name="add" size={20} color="#FFFFFF" />
              </TouchableOpacity>
            )}
            <TouchableOpacity style={styles.miniActionBtn}>
              <Ionicons name="search" size={18} color="#FFFFFF" />
            </TouchableOpacity>
          </View>
        </View>
      </Animated.View>

      <Animated.ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
        onScroll={Animated.event(
          [{ nativeEvent: { contentOffset: { y: scrollY } } }],
          { useNativeDriver: true }
        )}
        scrollEventThrottle={16}
      >
        {/* Full Header Content */}
        <Animated.View style={[styles.heroContent, { paddingTop: (insets?.top || 0) + 15, opacity: headerOpacity }]}>
          <View style={styles.navHeader}>
            <TouchableOpacity
              onPress={() => navigation.goBack()}
              style={styles.backCircle}
              hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
            >
              <Ionicons name="arrow-back" size={22} color="#FFFFFF" />
            </TouchableOpacity>
            <View style={styles.headerIcons}>
              {isMentorOrAdmin && (
                <TouchableOpacity
                  style={styles.addPill}
                  onPress={() => navigation.navigate('EventAdd')}
                  hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                  activeOpacity={0.7}
                >
                  <Ionicons name="add" size={18} color="#FFFFFF" />
                  <Caption style={styles.addText}>CREATE</Caption>
                </TouchableOpacity>
              )}
              <TouchableOpacity
                style={styles.iconCircle}
                hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
              >
                <Ionicons name="search" size={20} color="#FFFFFF" />
              </TouchableOpacity>
            </View>
          </View>

          <View style={styles.titleBlock}>
            <Caption style={styles.subtitle}>DISCOVER NEW</Caption>
            <H1 style={styles.mainTitle}>Community Events</H1>
          </View>
        </Animated.View>

        {/* Content Surface */}
        <View style={styles.contentSurface}>

          {/* Categories Pill Bar */}
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            style={styles.catScroll}
            contentContainerStyle={styles.catScrollContent}
          >
            {categories.map((cat) => (
              <TouchableOpacity
                key={cat.id}
                style={[styles.catPill, activeCategory === cat.name && styles.catPillActive]}
                onPress={() => setActiveCategory(cat.name)}
              >
                <Caption style={[styles.catLabel, activeCategory === cat.name && styles.catLabelActive]}>{cat.name}</Caption>
              </TouchableOpacity>
            ))}
          </ScrollView>

          {/* Events Feed Section */}
          <View style={styles.feedContainer}>
            <View style={styles.feedHeader}>
              <H2 style={styles.sectionTitle}>Upcoming For You</H2>
              <TouchableOpacity><Caption style={styles.linkBlue}>Sort by Date</Caption></TouchableOpacity>
            </View>

            {loading ? (
              <View style={styles.flatListContent}>
                {[1, 2, 3].map(i => (
                  <Skeleton key={i} width="100%" height={200} borderRadius={16} style={{ marginBottom: 12 }} />
                ))}
              </View>
            ) : (
              <FlatList
                data={events}
                renderItem={renderEventItem}
                keyExtractor={item => item.id.toString()}
                scrollEnabled={false}
                contentContainerStyle={styles.flatListContent}
                ListEmptyComponent={
                  <View style={styles.emptyContainer}>
                    <Ionicons name="calendar-outline" size={48} color="#E5E7EB" />
                    <Body2 style={{ color: '#9CA3AF', marginTop: 10 }}>No events found for this category</Body2>
                  </View>
                }
              />
            )}
          </View>

          <View style={styles.footer}>
            <Caption style={styles.footerText}>Youth Connect Events • Innovative Hub</Caption>
          </View>

        </View>
      </Animated.ScrollView>

      {/* Import StatusBar from expo-status-bar */}
      {/* (Done via imports at top) */}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#FFFFFF',
  },
  headerBg: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 0,
  },
  auraContainer: {
    ...StyleSheet.absoluteFillObject,
    overflow: 'hidden',
  },
  aura: {
    position: 'absolute',
    borderRadius: 100,
    opacity: 0.2,
  },
  aura1: {
    width: 200,
    height: 200,
    top: -50,
    right: -20,
    backgroundColor: '#C084FC',
  },
  aura2: {
    width: 150,
    height: 150,
    top: 50,
    left: -40,
    backgroundColor: '#818CF8',
  },
  stickyTopBar: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 100,
    height: 100,
    elevation: 4,
  },
  stickyContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    height: 50,
  },
  miniBackBtn: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: 'rgba(255,255,255,0.2)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  miniActionBtn: {
    width: 32,
    height: 32,
    alignItems: 'center',
    justifyContent: 'center',
  },
  stickyTitle: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '800',
  },
  scrollView: {
    flex: 1,
    zIndex: 1,
  },
  scrollContent: {
    paddingBottom: 0,
  },
  heroContent: {
    paddingHorizontal: 22,
    paddingBottom: 40,
  },
  navHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 30,
  },
  backCircle: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(255,255,255,0.2)',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10,
  },
  addPill: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#10B981',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 20,
    marginRight: 10,
    ...getShadow('sm'),
  },
  addText: {
    color: '#FFFFFF',
    fontWeight: '800',
    fontSize: 10,
    marginLeft: 4,
  },
  miniAddBtn: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#10B981',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 8,
  },
  iconCircle: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(255,255,255,0.15)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerIcons: {
    flexDirection: 'row',
    alignItems: 'center',
    zIndex: 100,
  },
  titleBlock: {
    marginTop: 5,
    paddingLeft: 4,
    marginBottom: -10,
  },
  subtitle: {
    color: 'rgba(255,255,255,0.8)',
    fontSize: 12,
    fontWeight: '800',
    letterSpacing: 2,
  },
  mainTitle: {
    color: '#FFFFFF',
    fontSize: 24,
    fontWeight: '850',
    marginTop: 4,
  },
  contentSurface: {
    backgroundColor: '#FFFFFF',
    borderTopLeftRadius: 35,
    borderTopRightRadius: 35,
    paddingTop: 30,
    minHeight: SCREEN_HEIGHT - 100,
    marginTop: -20,
    zIndex: 5,
  },
  catScroll: {
    maxHeight: 50,
    marginBottom: 20,
  },
  catScrollContent: {
    paddingLeft: 22,
    paddingRight: 10,
    alignItems: 'center',
  },
  catPill: {
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 15,
    backgroundColor: '#F9FAFB',
    marginRight: 10,
  },
  catPillActive: {
    backgroundColor: '#6366F1',
  },
  catLabel: {
    color: '#9CA3AF',
    fontWeight: '700',
  },
  catLabelActive: {
    color: '#FFFFFF',
  },
  feedContainer: {
    paddingHorizontal: 22,
    marginBottom: 30,
  },
  feedHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '800',
    color: '#111827',
  },
  linkBlue: {
    color: '#6366F1',
    fontWeight: '700',
  },
  flatListContent: {
    gap: 15,
  },
  eventCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: '#F1F5F9',
    ...getShadow('xs'),
    marginBottom: 12,
  },
  cardImageContainer: {
    height: 130,
    position: 'relative',
  },
  eventImage: {
    width: '100%',
    height: '100%',
  },
  imageOverlay: {
    ...StyleSheet.absoluteFillObject,
  },
  categoryBadgeStatic: {
    position: 'absolute',
    top: 15,
    left: 15,
    backgroundColor: 'rgba(255,255,255,0.9)',
    paddingHorizontal: 12,
    paddingVertical: 5,
    borderRadius: 10,
  },
  categoryBadgeText: {
    color: '#111827',
    fontWeight: '900',
    fontSize: 9,
  },
  eventInfo: {
    padding: 12,
  },
  titleRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 8,
  },
  pointsBadgeCompact: {
    backgroundColor: '#FEF3C7',
    paddingHorizontal: 6,
    paddingVertical: 3,
    borderRadius: 6,
    marginLeft: 8,
  },
  pointsTextCompact: {
    color: '#D97706',
    fontWeight: '800',
    fontSize: 9,
  },
  vibeTextCompact: {
    color: '#6366F1',
    fontSize: 9,
    fontWeight: '800',
    textTransform: 'uppercase',
    marginTop: 2,
  },
  emptyContainer: {
    padding: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },
  eventTitleText: {
    fontSize: 15,
    fontWeight: '800',
    color: '#1e293b',
    flex: 1,
  },
  favBtn: {
    marginLeft: 10,
  },
  compactDetails: {
    flexDirection: 'row',
    gap: 15,
    marginBottom: 18,
  },
  detailItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
  },
  detailText: {
    color: '#9CA3AF',
    fontWeight: '600',
    fontSize: 11,
  },
  joinBtn: {
    backgroundColor: '#6366F1',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    borderRadius: 14,
    gap: 8,
  },
  joinBtnText: {
    color: '#FFFFFF',
    fontWeight: '800',
    fontSize: 12,
  },
  suggestBox: {
    marginHorizontal: 22,
    marginBottom: 40,
  },
  suggestCard: {
    borderRadius: 24,
    padding: 20,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 15,
  },
  suggestIcon: {
    width: 44,
    height: 44,
    borderRadius: 12,
    backgroundColor: '#FFFFFF',
    alignItems: 'center',
    justifyContent: 'center',
    ...getShadow('xs'),
  },
  suggestInfo: {
    flex: 1,
  },
  suggestTitle: {
    fontSize: 15,
    fontWeight: '800',
    color: '#1F2937',
  },
  suggestDesc: {
    fontSize: 11,
    color: '#6B7280',
    lineHeight: 16,
    marginTop: 2,
  },
  addBtn: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: '#EC4899',
    alignItems: 'center',
    justifyContent: 'center',
    ...getShadow('sm'),
  },
  footer: {
    alignItems: 'center',
    paddingBottom: 40,
    opacity: 0.3,
  },
  footerText: {
    fontSize: 10,
  },
  pastBadge: {
    position: 'absolute',
    top: 10,
    right: 10,
    backgroundColor: 'rgba(55, 65, 81, 0.9)',
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 8,
  },
  pastBadgeText: {
    color: '#FFFFFF',
    fontWeight: '800',
    fontSize: 9,
  },
});

export default EventsScreen;
